import { useCallback, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../components/providers/AuthProvider";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { captureEventAndRedirect } from "../utils/analytics";
import { PH_EVENTS_WEB } from "../utils/analyticsEvents";
import { api } from "../utils/api";
import {
  createCartAndGetCheckoutUrl,
  getProductByHandle,
} from "../utils/shopify";

export default function CheckoutProduct() {
  const { handle } = useParams<{ handle: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const posthog = usePostHog();

  console.log("🔍 CheckoutProduct: Component mounted/re-rendered", {
    handle,
    currentURL: window.location.href,
    user: !!user,
    loading,
  });
  const { handleError } = useErrorHandler({
    fallbackMessage: "Failed to load user information for checkout.",
  });

  // Fetch user email for prefilling Shopify checkout
  const {
    data: userEmail,
    isLoading: emailLoading,
    error: emailError,
  } = api.user.queryUserEmail.useQuery(undefined, {
    enabled: !!user && !loading,
  });

  // Handle email query errors using useEffect
  useEffect(() => {
    if (emailError) {
      handleError(emailError);
    }
  }, [emailError, handleError]);

  const handleCheckout = useCallback(async () => {
    console.log("🔍 CheckoutProduct: handleCheckout called", {
      handle,
      user: !!user,
      userEmail,
      processing,
    });
    if (!handle) {
      setError("Product not found");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Fetch product details
      const product = await getProductByHandle(handle);

      if (!product) {
        setError("Product not found");
        return;
      }

      // Get the first available variant
      const availableVariant = product.variants.find((v) => v.availableForSale);

      if (!availableVariant) {
        setError("Product is not available for sale");
        return;
      }

      // Create cart and get checkout URL with email prefilling
      // For unauthenticated users, pass undefined for user ID
      const checkoutUrl = await createCartAndGetCheckoutUrl(
        availableVariant.id,
        1, // Default quantity
        user ?? undefined, // Use user ID if authenticated, undefined if not
        userEmail ?? undefined, // Pass user email for prefilling (convert null to undefined)
      );

      if (checkoutUrl) {
        // Capture checkout initiation event and redirect
        console.log("🔍 CheckoutProduct: About to capture event and redirect", {
          eventName: PH_EVENTS_WEB.CHECKOUT_INITIATED,
          checkoutUrl,
          posthogReady: !!posthog,
          posthogMethods: posthog ? Object.keys(posthog) : [],
        });

        // Use the improved captureEventAndRedirect function
        await captureEventAndRedirect(
          posthog,
          PH_EVENTS_WEB.CHECKOUT_INITIATED,
          checkoutUrl,
          {
            channel: "shopify",
            product_handle: handle,
          },
        );
      } else {
        setError("Unable to create checkout. Please try again.");
      }
    } catch (err) {
      console.error("Error creating checkout:", err);
      setError("Unable to create checkout. Please try again.");
    } finally {
      setProcessing(false);
    }
  }, [handle, user, userEmail, posthog, navigate]);

  useEffect(() => {
    console.log("🔍 CheckoutProduct: useEffect triggered", {
      loading,
      user: !!user,
      handle,
      processing,
      emailLoading,
      userEmail,
    });

    // If we have a handle and not currently processing, start checkout process
    if (handle && !processing && !loading && !emailLoading) {
      console.log("🔍 CheckoutProduct: Starting checkout process", {
        authenticated: !!user,
        userEmail: userEmail || "none",
      });
      handleCheckout();
    } else {
      console.log("🔍 CheckoutProduct: Not ready for checkout", {
        user: !!user,
        handle: !!handle,
        processing,
        loading,
        emailLoading,
      });
    }
  }, [user, loading, handle, emailLoading, navigate, handleCheckout]);

  // Show loading while checking auth or processing
  if (loading || processing) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <div className="loading-spinner"></div>
            <p>{processing ? "Creating checkout..." : "Loading..."}</p>
          </div>
        </div>
      </div>
    );
  }

  // If there's an error, show it with option to go back
  if (error) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-error">
            <h2>Checkout Error</h2>
            <p>{error}</p>
            <button
              onClick={() => navigate("/purchase")}
              className="btn-primary"
            >
              Back to Purchase
            </button>
          </div>
        </div>
      </div>
    );
  }

  // This shouldn't be reached, but just in case
  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
          <p>Preparing checkout...</p>
        </div>
      </div>
    </div>
  );
}
